import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  FileText,
  Grid2x2,
  Keyboard,
  PackageCheck,
  PackageX,
  ScanBarcode,
  Search,
  Sigma,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Textarea } from '../components/ui/Textarea';
import { Modal, touch } from '../components/receiving/Modal';
import { DestinationCard, type CardState } from '../components/receiving/DestinationCard';
import { EmployeeBar } from '../components/receiving/EmployeeBar';
import { ExpectedList } from '../components/receiving/ExpectedList';
import { BinsBoard } from '../components/receiving/BinsBoard';
import { AnomalyRegisterView } from '../components/receiving/AnomalyRegisterView';
import { CallListView } from '../components/receiving/CallListView';
import { VanPanel } from '../components/receiving/VanPanel';
import { JournalView } from '../components/receiving/JournalView';
import { ByPurchaseOrder } from './Reception';
import { useAudioFeedback } from '../hooks/useAudioFeedback';
import { useOnline } from '../hooks/useOnline';
import { useAuthStore } from '../stores/authStore';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import apiClient from '../api/client';
import {
  apiMessage,
  enqueueScan,
  fmtTime,
  isNetworkError,
  readQueue,
  receivingApi,
  rememberCurrentEmployee,
  replayQueue,
  type AmbiguousScan,
  type ExpectedList as Expected,
  type ScanLog,
  type ScanResponse,
  type VanBoard,
  type VanSummary,
  type PickLineScan,
  type PackConfirmScan,
} from '../lib/receiving';
import { cn } from '../utils/cn';

type Tab = 'expected' | 'scan' | 'bins' | 'close' | 'anomalies' | 'journal' | 'byPo';
const TAB_KEY = 'mecadepot.receiving.tab';
const ROLE_KEY: Record<string, string> = {
  OWNER: 'Owner',
  MANAGER: 'Manager',
  WAREHOUSE_KEEPER: 'Warehouse',
  POLYVALENT: 'Polyvalent',
  COUNTER_AGENT: 'Counter',
  PURCHASER: 'Purchaser',
  ACCOUNTING: 'Accounting',
  ADMIN: 'Admin',
};

/**
 * The warehouse tablet (spec §4–7, §10): landscape, 18 px text, 84 px slot, 48 px targets, no
 * information on hover only. The scan field keeps the focus and takes a hardware scanner
 * (inputmode none), with a button for the on-screen numeric keyboard. The page only displays
 * what the server returns — every rule runs server-side.
 */
export default function ReceivingTablet() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const role = useCapabilitiesStore((s) => s.data?.role) ?? user?.role ?? '';
  const loadCaps = useCapabilitiesStore((s) => s.fetch);
  const online = useOnline();
  const audio = useAudioFeedback();

  const [tab, setTab] = useState<Tab>(() => {
    try {
      return (localStorage.getItem(TAB_KEY) as Tab) || 'scan';
    } catch {
      return 'scan';
    }
  });
  const [me, setMe] = useState<{ name: string }>({ name: user?.email ?? '' });
  const [expected, setExpected] = useState<Expected | null>(null);
  const [log, setLog] = useState<ScanLog | null>(null);
  const [card, setCard] = useState<CardState>({ kind: 'IDLE' });
  const [pending, setPending] = useState(readQueue().length);
  const [code, setCode] = useState('');
  const [numericKeyboard, setNumericKeyboard] = useState(false);
  const [countMode, setCountMode] = useState(false);
  const [damagedMode, setDamagedMode] = useState(false);
  const [qtyFor, setQtyFor] = useState<string | null>(null);
  const [qty, setQty] = useState('');
  const [ambiguous, setAmbiguous] = useState<
    (AmbiguousScan & { qty?: number; damaged?: boolean }) | null
  >(null);
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoReason, setUndoReason] = useState('');
  const [undoError, setUndoError] = useState('');
  const [whereQ, setWhereQ] = useState('');
  const [where, setWhere] = useState<Array<Record<string, unknown>> | null>(null);
  const [whereErr, setWhereErr] = useState('');
  const [closeResult, setCloseResult] = useState<string>('');
  const [binsKey, setBinsKey] = useState(0);
  const [vans, setVans] = useState<VanBoard | null>(null);
  const [vanEnded, setVanEnded] = useState<VanSummary | null>(null);
  const [vanBusy, setVanBusy] = useState(false);
  /** The scan the server refused as a probable double scan, waiting for "it is a second part". */
  const [doubleScan, setDoubleScan] = useState<{ code: string } | null>(null);
  /** Migration 138: an unknown code — the operator says which expected part it is. */
  const [pickLine, setPickLine] = useState<
    (PickLineScan & { qty?: number; damaged?: boolean }) | null
  >(null);
  /** Migration 138: a pack code — "1 scan = N units", confirmed once. */
  const [pack, setPack] = useState<(PackConfirmScan & { qty?: number; damaged?: boolean }) | null>(
    null
  );
  const scanField = useRef<HTMLInputElement>(null);
  const dialogOpen =
    qtyFor !== null || ambiguous !== null || pickLine !== null || pack !== null || undoOpen;

  const choose = (k: Tab) => {
    setTab(k);
    try {
      localStorage.setItem(TAB_KEY, k);
    } catch {
      /* not remembered */
    }
  };

  const refreshMe = useCallback(() => {
    apiClient
      .get<{ data: { fullName: string | null; email: string } }>('/me/profile')
      .then(({ data }) => {
        const name = data.data.fullName || data.data.email;
        setMe({ name });
        rememberCurrentEmployee(name, useAuthStore.getState().user?.role ?? '');
      })
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    refreshMe();
  }, [refreshMe, user?.sub]);

  const loadExpected = useCallback(() => {
    receivingApi
      .expected()
      .then(setExpected)
      .catch(() => undefined);
  }, []);
  const loadLog = useCallback(() => {
    receivingApi
      .scanLog()
      .then(setLog)
      .catch(() => undefined);
  }, []);
  const loadVans = useCallback(() => {
    receivingApi
      .vans()
      .then(setVans)
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    if (tab === 'expected') loadExpected();
    if (tab === 'scan') {
      loadLog();
      loadVans();
    }
  }, [tab, loadExpected, loadLog, loadVans]);

  const startVan = async (supplierId: string) => {
    setVanBusy(true);
    try {
      await receivingApi.startVan(supplierId);
      setVanEnded(null);
      setDoubleScan(null);
      setCard({ kind: 'IDLE' });
      loadVans();
    } catch (err) {
      setCard({ kind: 'REFUSED', message: apiMessage(err) });
      audio.error();
    } finally {
      setVanBusy(false);
    }
  };

  /* Spec §5: "asks for confirmation if the operator ends the van while units are still
     expected" — the server says so (409 VAN_STILL_EXPECTED) and names the count. */
  const endVan = async () => {
    setVanBusy(true);
    try {
      let summary: VanSummary;
      try {
        summary = await receivingApi.endVan();
      } catch (err) {
        const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
        if (code !== 'VAN_STILL_EXPECTED' || !window.confirm(apiMessage(err))) throw err;
        summary = await receivingApi.endVan(true);
      }
      setVanEnded(summary);
      setDoubleScan(null);
      setCard({ kind: 'IDLE' });
      loadVans();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      if (code !== 'VAN_STILL_EXPECTED') setCard({ kind: 'REFUSED', message: apiMessage(err) });
    } finally {
      setVanBusy(false);
    }
  };

  const focusScan = useCallback(() => {
    if (tab === 'scan' && !dialogOpen) scanField.current?.focus({ preventScroll: true });
  }, [tab, dialogOpen]);
  useEffect(() => {
    focusScan();
  }, [focusScan, card]);

  const showCard = useCallback(
    (result: ScanResponse, fromQueue = false) => {
      if (
        result.kind === 'AMBIGUOUS' ||
        result.kind === 'DOUBLE_SCAN' ||
        result.kind === 'PICK_LINE' ||
        result.kind === 'PACK_CONFIRM'
      )
        return;
      setCard({ kind: 'CARD', card: result, fromQueue });
      // A distinct sound for anything that went to Zone Q (spec §10).
      if (result.kind === 'CUSTOMER' || result.kind === 'REPLENISHMENT') {
        if (result.quarantine) audio.wrongProduct();
        else audio.success();
      } else {
        audio.wrongProduct();
      }
    },
    [audio]
  );

  // Network back: replay the queue once, each scan in its own employee's name.
  useEffect(() => {
    if (!online || readQueue().length === 0) return;
    void replayQueue().then(({ sent, failed }) => {
      setPending(readQueue().length);
      const last = [...sent]
        .reverse()
        .find(
          (r) =>
            r.kind !== 'AMBIGUOUS' &&
            r.kind !== 'DOUBLE_SCAN' &&
            r.kind !== 'PICK_LINE' &&
            r.kind !== 'PACK_CONFIRM'
        );
      if (last) showCard(last, true);
      if (failed.length)
        setCard({
          kind: 'REFUSED',
          message: failed.map((f) => `${f.item.body.code}: ${f.message}`).join(' · '),
        });
      loadLog();
    });
  }, [online, showCard, loadLog]);

  const send = async (body: {
    code: string;
    qty?: number;
    damaged?: boolean;
    productId?: string;
    confirmSecond?: boolean;
    learnLine?: boolean;
    unknownConfirmed?: boolean;
    confirmPack?: boolean;
  }) => {
    const commandId = crypto.randomUUID();
    setDoubleScan(null);
    if (!navigator.onLine) {
      enqueueScan({
        commandId,
        body,
        userId: user?.sub ?? '',
        userName: me.name,
        queuedAt: new Date().toISOString(),
      });
      setPending(readQueue().length);
      setCard({ kind: 'QUEUED', pending: readQueue().length, by: me.name });
      audio.success();
      return;
    }
    try {
      const r = await receivingApi.scan(body, commandId);
      if (r.kind === 'AMBIGUOUS') {
        setAmbiguous({ ...r, qty: body.qty, damaged: body.damaged });
        audio.error();
        return;
      }
      if (r.kind === 'DOUBLE_SCAN') {
        setDoubleScan({ code: body.code });
        audio.error();
        return;
      }
      if (r.kind === 'PICK_LINE') {
        setPickLine({ ...r, qty: body.qty, damaged: body.damaged });
        audio.error();
        return;
      }
      if (r.kind === 'PACK_CONFIRM') {
        setPack({ ...r, qty: body.qty, damaged: body.damaged });
        return;
      }
      showCard(r);
      if (r.van !== undefined) setVans((v) => (v ? { ...v, current: r.van ?? null } : v));
      loadLog();
    } catch (err) {
      if (isNetworkError(err)) {
        enqueueScan({
          commandId,
          body,
          userId: user?.sub ?? '',
          userName: me.name,
          queuedAt: new Date().toISOString(),
        });
        setPending(readQueue().length);
        setCard({ kind: 'QUEUED', pending: readQueue().length, by: me.name });
        return;
      }
      const data = (
        err as {
          response?: {
            data?: { code?: string; details?: { supplierName?: string; productLabel?: string } };
          };
        }
      ).response?.data;
      const code = data?.code;
      // Expected on another supplier's truck: name it, so the operator switches van (2026-10-27).
      setCard({
        kind: 'REFUSED',
        message:
          code === 'WRONG_VAN' && data?.details
            ? t('rcv.wrongVan', {
                part: data.details.productLabel ?? '',
                supplier: data.details.supplierName ?? '',
              })
            : apiMessage(err),
      });
      audio.error();
      // The van may have been ended meanwhile (the day's close ends open vans).
      if (code === 'NO_VAN') loadVans();
    }
  };

  const submitCode = (raw: string) => {
    const c = raw.trim();
    setCode('');
    if (!c) return;
    if (countMode || damagedMode) {
      setQty('');
      setQtyFor(c);
      return;
    }
    void send({ code: c });
  };

  const confirmQty = () => {
    const n = parseInt(qty, 10);
    if (!(n > 0) || !qtyFor) return;
    const c = qtyFor;
    setQtyFor(null);
    void send({ code: c, qty: n, damaged: damagedMode || undefined });
    if (damagedMode) setDamagedMode(false);
  };

  const undo = async () => {
    setUndoError('');
    try {
      const r = await receivingApi.undoLast(undoReason);
      setUndoOpen(false);
      setUndoReason('');
      setCard({ kind: 'UNDONE', card: r.card });
      loadLog();
    } catch (err) {
      setUndoError(apiMessage(err));
    }
  };

  const lastLive = log?.data.find((r) => !r.undoneAt);

  const tabs: Array<[Tab, string, LucideIcon]> = [
    ['expected', t('rcv.tabs.expected'), ClipboardList],
    ['scan', t('rcv.tabs.scan'), ScanBarcode],
    ['bins', t('rcv.tabs.bins'), Grid2x2],
    ['close', t('rcv.tabs.close'), CheckCircle2],
    ['anomalies', t('rcv.tabs.anomalies'), AlertTriangle],
    ['journal', t('rcv.tabs.journal'), FileText],
    ['byPo', t('rcv.tabs.byPo'), PackageCheck],
  ];
  /* The day's four steps (receiving prototype header): a van is chosen → scanning; vans came
     and none is open → between vans / end of van; the day is closed → done. */
  const dayStep = vans?.dayClosed
    ? 4
    : vans?.current
      ? 2
      : vans?.suppliers.some((s) => s.vansToday > 0)
        ? 3
        : 1;
  const stepNames = [
    t('rcv.steps.van'),
    t('rcv.steps.scan'),
    t('rcv.steps.endVan'),
    t('rcv.steps.closeDay'),
  ];

  return (
    <div className="mx-auto grid max-w-[1500px] gap-3 text-lg md:grid-cols-[96px_minmax(0,1fr)]">
      {/* The tablet's left rail: one large target per screen (48 px+, icon and label). */}
      <nav
        role="tablist"
        aria-label={t('rcv.tabs.label')}
        className="flex gap-1 overflow-x-auto rounded-xl border bg-card p-1 md:sticky md:top-2 md:h-fit md:flex-col"
      >
        {tabs.map(([k, label, Icon]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => choose(k)}
            className={cn(
              'flex min-h-[72px] min-w-[80px] flex-col items-center justify-center gap-1 rounded-lg px-1 text-xs font-semibold',
              tab === k ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
            )}
          >
            <Icon className="h-6 w-6" />
            {label}
          </button>
        ))}
      </nav>
      <div className="min-w-0 space-y-3">
        <EmployeeBar
          name={me.name}
          roleLabel={t(`rcv.role.${ROLE_KEY[role] ?? role}`, { defaultValue: role })}
          online={online}
          pending={pending}
          onSwitched={() => {
            void loadCaps?.();
            refreshMe();
            setCard({ kind: 'IDLE' });
            loadLog();
          }}
        >
          {tab === 'scan' && vans && (
            <ol className="flex flex-wrap gap-1" aria-label={t('rcv.steps.label')}>
              {stepNames.map((n, i) => (
                <li
                  key={n}
                  aria-current={i + 1 === dayStep ? 'step' : undefined}
                  className={cn(
                    'flex min-h-10 items-center rounded-full border px-3 text-sm font-semibold',
                    i + 1 < dayStep &&
                      'border-green-600 bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400',
                    i + 1 === dayStep && 'border-primary bg-primary text-primary-foreground',
                    i + 1 > dayStep && 'text-muted-foreground'
                  )}
                >
                  {i + 1} · {n}
                </li>
              ))}
            </ol>
          )}
        </EmployeeBar>

        {tab === 'expected' && <ExpectedList data={expected} />}

        {tab === 'scan' && (
          <div className="grid gap-4 lg:grid-cols-12">
            <div className="space-y-3 lg:col-span-8">
              <VanPanel
                board={vans}
                ended={vanEnded}
                busy={vanBusy}
                onStart={(id) => void startVan(id)}
                onEnd={() => void endVan()}
                onGoClose={() => choose('close')}
              />
              {vans?.current && (
                <>
                  <form
                    className="flex flex-wrap items-end gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      submitCode(code);
                    }}
                  >
                    <div className="min-w-[14rem] flex-1">
                      <Label htmlFor="scan" className="sr-only">
                        {t('rcv.scan.barcode')}
                      </Label>
                      <Input
                        id="scan"
                        ref={scanField}
                        autoComplete="off"
                        inputMode={numericKeyboard ? 'numeric' : 'none'}
                        className="h-14 text-2xl tracking-wide"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder={t('rcv.scan.placeholder')}
                      />
                    </div>
                    <Button type="submit" className={cn(touch, 'h-14')}>
                      {t('rcv.scan.enter')}
                    </Button>
                  </form>
                  {doubleScan && (
                    <div
                      role="alert"
                      className="rounded-xl border-l-4 border-amber-500 bg-amber-50 px-4 py-3 dark:bg-amber-950/30"
                    >
                      <div className="text-xl font-semibold">{t('rcv.double.title')}</div>
                      <p className="text-sm">{t('rcv.double.hint', { code: doubleScan.code })}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button
                          className={touch}
                          onClick={() => void send({ code: doubleScan.code, confirmSecond: true })}
                        >
                          {t('rcv.double.second')}
                        </Button>
                        <Button
                          variant="outline"
                          className={touch}
                          onClick={() => setDoubleScan(null)}
                        >
                          {t('rcv.double.same')}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
              <DestinationCard state={card} />
            </div>
            <div className="space-y-3 lg:col-span-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={countMode ? 'default' : 'outline'}
                  className={touch}
                  aria-pressed={countMode}
                  onClick={() => setCountMode((v) => !v)}
                >
                  <Sigma className="h-5 w-5" /> {t('rcv.scan.counted')}
                </Button>
                <Button
                  type="button"
                  variant={damagedMode ? 'destructive' : 'outline'}
                  className={touch}
                  aria-pressed={damagedMode}
                  onClick={() => setDamagedMode((v) => !v)}
                >
                  <PackageX className="h-5 w-5" /> {t('rcv.scan.damaged')}
                </Button>
                <Button
                  type="button"
                  variant={numericKeyboard ? 'secondary' : 'outline'}
                  className={touch}
                  aria-pressed={numericKeyboard}
                  onClick={() => {
                    setNumericKeyboard((v) => !v);
                    setTimeout(() => scanField.current?.focus(), 0);
                  }}
                >
                  <Keyboard className="h-5 w-5" /> {t('rcv.scan.keyboard')}
                </Button>
              </div>
              {log && (
                <div className="grid grid-cols-4 gap-2 text-center">
                  {(
                    [
                      ['units', log.stats.units],
                      ['toCustomers', log.stats.toCustomers],
                      ['toStock', log.stats.toStock],
                      ['toQuarantine', log.stats.toQuarantine],
                    ] as const
                  ).map(([k, v]) => (
                    <div key={k} className="rounded-xl border bg-card px-2 py-2">
                      <div className="text-2xl font-bold tabular-nums">{v}</div>
                      <div className="text-xs text-muted-foreground">{t(`rcv.stats.${k}`)}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-xl border bg-card">
                <div className="flex items-center justify-between border-b px-4 py-2">
                  <h3 className="text-lg font-semibold">{t('rcv.log.title')}</h3>
                  <Button
                    variant="outline"
                    className={touch}
                    disabled={!lastLive}
                    onClick={() => {
                      setUndoReason('');
                      setUndoError('');
                      setUndoOpen(true);
                    }}
                  >
                    <Undo2 className="h-5 w-5" /> {t('rcv.log.undo')}
                  </Button>
                </div>
                <ul className="max-h-[420px] divide-y overflow-y-auto">
                  {log?.data.length === 0 && (
                    <li className="px-4 py-3 text-base text-muted-foreground">
                      {t('rcv.log.empty')}
                    </li>
                  )}
                  {log?.data.map((r) => (
                    <li
                      key={r.id}
                      className={cn(
                        'px-4 py-2 text-base',
                        r.undoneAt && 'text-muted-foreground line-through decoration-1'
                      )}
                    >
                      <span className="mr-2 font-mono text-sm">{fmtTime(r.ts)}</span>
                      <strong>{r.productName ?? r.code}</strong> × {r.qty}
                      <div className="text-sm">
                        {r.destination === 'CUSTOMER'
                          ? `${r.slot} · ${r.customerName ?? ''} · ${r.orderNumber ?? ''}`
                          : r.destination === 'REPLENISHMENT'
                            ? t('rcv.log.toShelf', { location: r.location ?? '—' })
                            : t('rcv.log.toQ', {
                                why: t(`rcv.reason.${r.reason ?? 'SURPLUS'}`),
                                ano: r.anomaly ?? '',
                              })}
                        {' · '}
                        {t('rcv.by', { name: r.by })}
                      </div>
                      {r.undoneAt && (
                        <div className="text-sm no-underline">
                          {t('rcv.log.undone', {
                            name: r.undoneBy ?? '',
                            time: fmtTime(r.undoneAt),
                            reason: r.undoReason ?? '',
                          })}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border bg-card p-4">
                <h3 className="mb-2 text-lg font-semibold">{t('rcv.where.title')}</h3>
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setWhereErr('');
                    receivingApi
                      .whereIs(whereQ)
                      .then((r) => setWhere(r.data))
                      .catch((err) => {
                        setWhere(null);
                        setWhereErr(apiMessage(err));
                      });
                  }}
                >
                  <Input
                    className="h-12 text-base"
                    value={whereQ}
                    onChange={(e) => setWhereQ(e.target.value)}
                    placeholder={t('rcv.where.placeholder')}
                  />
                  <Button type="submit" variant="outline" className={touch}>
                    <Search className="h-5 w-5" />
                  </Button>
                </form>
                {whereErr && <p className="mt-2 text-base text-destructive">{whereErr}</p>}
                {where?.map((p) => (
                  <div key={String(p.id)} className="mt-3 space-y-1 text-base">
                    <div>
                      <strong>{String(p.name)}</strong> — {String(p.brand ?? '')} ·{' '}
                      {String(p.ref ?? '')}
                    </div>
                    <div>
                      {t('rcv.where.shelf', {
                        shelf: String(p.shelf ?? '—'),
                        n: Number(p.available ?? 0),
                      })}
                    </div>
                    {(p.bins as Array<Record<string, unknown>>).length === 0 ? (
                      <div className="text-muted-foreground">{t('rcv.where.noBin')}</div>
                    ) : (
                      (p.bins as Array<Record<string, unknown>>).map((b, i) => (
                        <div key={i}>
                          {t('rcv.where.bin', {
                            slot: String(b.slot),
                            customer: String(b.customerName ?? ''),
                            order: String(b.orderNumber),
                            qty: Number(b.qty),
                            by: String(b.orderedBy),
                          })}
                        </div>
                      ))
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'bins' && <BinsBoard key={binsKey} role={role} />}

        {tab === 'close' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 rounded-xl border bg-card p-4">
              <Button
                className={cn(touch, 'h-14 text-lg')}
                onClick={() => {
                  receivingApi
                    .closeDay()
                    .then((r) => {
                      setCloseResult(
                        r.alreadyClosed
                          ? t('rcv.close.already', { br: r.brNumber })
                          : t('rcv.close.done', {
                              count: r.shortagesCreated,
                              br: r.brNumber,
                              name: me.name,
                            })
                      );
                      setBinsKey((k) => k + 1);
                      loadVans(); // the close ends any van left open
                    })
                    .catch((err) => setCloseResult(apiMessage(err)));
                }}
              >
                {t('rcv.close.button')}
              </Button>
              <p className="max-w-2xl text-base text-muted-foreground">{t('rcv.close.hint')}</p>
            </div>
            {closeResult && (
              <p className="rounded-lg bg-muted px-4 py-3 text-base" role="status">
                {closeResult}
              </p>
            )}
            <CallListView key={closeResult} meName={me.name} />
          </div>
        )}

        {tab === 'anomalies' && <AnomalyRegisterView role={role} />}
        {tab === 'journal' && <JournalView />}
        {tab === 'byPo' && (
          <div className="text-base">
            <ByPurchaseOrder />
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          <Link to="/anomalies" className="underline">
            {t('rcv.links.anomalies')}
          </Link>
        </p>

        {/* Counted quantity / damaged quantity */}
        <Modal
          open={qtyFor !== null}
          title={damagedMode ? t('rcv.qty.damagedTitle') : t('rcv.qty.countedTitle')}
          subtitle={qtyFor ?? ''}
          onClose={() => setQtyFor(null)}
          footer={
            <>
              <Button variant="ghost" className={touch} onClick={() => setQtyFor(null)}>
                {t('rcv.cancel')}
              </Button>
              <Button className={touch} disabled={!(parseInt(qty, 10) > 0)} onClick={confirmQty}>
                {t('rcv.qty.confirm')}
              </Button>
            </>
          }
        >
          <Input
            inputMode="numeric"
            className="h-16 text-center text-4xl"
            value={qty}
            onChange={(e) => setQty(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmQty();
            }}
          />
          <div className="mt-3 grid grid-cols-3 gap-2">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'].map((k) => (
              <button
                key={k}
                type="button"
                className="min-h-14 rounded-lg border text-2xl font-semibold hover:bg-accent"
                onClick={() =>
                  setQty((v) =>
                    k === 'C' ? '' : k === '⌫' ? v.slice(0, -1) : (v + k).replace(/^0+/, '')
                  )
                }
              >
                {k}
              </button>
            ))}
          </div>
        </Modal>

        {/* An ambiguous code: the operator picks the part by eye. */}
        <Modal
          open={ambiguous !== null}
          title={t('rcv.ambiguous.title')}
          subtitle={ambiguous?.code}
          onClose={() => setAmbiguous(null)}
        >
          <div className="space-y-2">
            {ambiguous?.candidates.map((c) => (
              <button
                key={c.productId}
                type="button"
                className="flex min-h-12 w-full items-center justify-between rounded-lg border px-4 text-left text-base hover:bg-accent"
                onClick={() => {
                  const a = ambiguous;
                  setAmbiguous(null);
                  void send({
                    code: a.code,
                    qty: a.qty,
                    damaged: a.damaged,
                    productId: c.productId,
                  });
                }}
              >
                <span>
                  <strong>{c.name}</strong> — {c.brand}
                </span>
                <span className="text-muted-foreground">{c.sku}</span>
              </button>
            ))}
          </div>
        </Modal>

        {/* Migration 138 — an unknown code: which of this van's expected parts is it? The code is
            learned for that part, so the next box of it scans by itself. */}
        <Modal
          open={pickLine !== null}
          title={t('rcv.pickLine.title')}
          subtitle={pickLine?.code}
          onClose={() => setPickLine(null)}
        >
          {pickLine?.unexpectedProduct ? (
            // Feedback 3.12 — the part is known but nobody ordered it: say so, offer the van's
            // lines, and let the operator close with nothing written.
            <p className="mb-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
              {t('rcv.pickLine.notOrdered', {
                ref: pickLine.unexpectedProduct.sku,
                name: pickLine.unexpectedProduct.name,
              })}
            </p>
          ) : (
            <p className="mb-2 text-sm text-muted-foreground">{t('rcv.pickLine.hint')}</p>
          )}
          <div className="max-h-[50vh] space-y-2 overflow-auto">
            {pickLine?.candidates.map((c) => (
              <button
                key={c.productId}
                type="button"
                className="flex min-h-12 w-full items-center justify-between gap-3 rounded-lg border px-4 text-left text-base hover:bg-accent"
                onClick={() => {
                  const a = pickLine;
                  setPickLine(null);
                  void send({
                    code: a.code,
                    qty: a.qty,
                    damaged: a.damaged,
                    productId: c.productId,
                    learnLine: true,
                  });
                }}
              >
                <span>
                  <strong>{c.brand ?? '—'}</strong> {c.reference}
                  <span className="ml-2 text-sm text-muted-foreground">{c.name}</span>
                </span>
                <span className="shrink-0 text-sm text-muted-foreground">
                  {t('rcv.pickLine.outstanding', { n: c.outstanding })}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="mt-3 min-h-12 w-full rounded-lg border border-destructive/40 px-4 text-base text-destructive hover:bg-destructive/10"
            onClick={() => {
              const a = pickLine;
              setPickLine(null);
              if (a)
                void send({ code: a.code, qty: a.qty, damaged: a.damaged, unknownConfirmed: true });
            }}
          >
            {t('rcv.pickLine.none')}
          </button>
        </Modal>

        {/* Migration 138 — a pack code, confirmed the first time. */}
        <Modal
          open={pack !== null}
          title={t('rcv.pack.title', { n: pack?.packQty ?? 0 })}
          subtitle={pack?.productName ?? pack?.code}
          onClose={() => setPack(null)}
        >
          <p className="mb-3 text-base">{t('rcv.pack.hint', { n: pack?.packQty ?? 0 })}</p>
          <div className="flex gap-2">
            <button
              type="button"
              className="min-h-12 flex-1 rounded-lg bg-primary px-4 text-base font-semibold text-primary-foreground"
              onClick={() => {
                const a = pack;
                setPack(null);
                if (a)
                  void send({
                    code: a.code,
                    qty: a.qty,
                    damaged: a.damaged,
                    productId: a.productId,
                    confirmPack: true,
                  });
              }}
            >
              {t('rcv.pack.confirm', { n: pack?.packQty ?? 0 })}
            </button>
            <button
              type="button"
              className="min-h-12 flex-1 rounded-lg border px-4 text-base"
              onClick={() => setPack(null)}
            >
              {t('common.cancel')}
            </button>
          </div>
        </Modal>

        {/* Undo last scan: a reason of 10 characters or more. */}
        <Modal
          open={undoOpen}
          title={t('rcv.log.undoTitle')}
          subtitle={
            lastLive
              ? `${fmtTime(lastLive.ts)} · ${lastLive.productName ?? lastLive.code} × ${lastLive.qty} · ${t('rcv.by', { name: lastLive.by })}`
              : ''
          }
          onClose={() => setUndoOpen(false)}
          footer={
            <>
              <Button variant="ghost" className={touch} onClick={() => setUndoOpen(false)}>
                {t('rcv.cancel')}
              </Button>
              <Button
                variant="destructive"
                className={touch}
                disabled={undoReason.trim().length < 10}
                onClick={() => void undo()}
              >
                {t('rcv.log.undoConfirm')}
              </Button>
            </>
          }
        >
          <Label htmlFor="undo-r" className="text-base">
            {t('rcv.reason10')}
          </Label>
          <Textarea
            id="undo-r"
            rows={3}
            className="text-base"
            value={undoReason}
            onChange={(e) => setUndoReason(e.target.value)}
          />
          {undoError && (
            <p className="mt-2 text-base text-destructive" role="alert">
              {undoError}
            </p>
          )}
        </Modal>
      </div>
    </div>
  );
}
